import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { View } from 'react-native';

import type { UseWidgetDialogOptions } from './types';

// The iframe itself carries role="dialog" on the host page; inside the frame
// any element with the role is an overlay that owns Escape.
const hasOpenDialog = (): boolean =>
  document.querySelector('[role="dialog"]') !== null;

export const useWidgetDialog = (
  ref: RefObject<View | null>,
  { shownCount, closeWidget }: UseWidgetDialogOptions,
): void => {
  const closeWidgetRef = useRef(closeWidget);
  closeWidgetRef.current = closeWidget;

  useEffect(() => {
    if (shownCount === 0) return;
    const frame = requestAnimationFrame(() => {
      (ref.current as unknown as HTMLElement | null)?.focus?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [ref, shownCount]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || hasOpenDialog()) return;
      closeWidgetRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
};
